import { CalendarClock, Wallet, Lock } from "lucide-react";
import Reveal from "@/components/Reveal";

const CARD_IMG =
  "https://images.unsplash.com/photo-1772442364499-52f96e8fc4d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwY2VyYW1pYyUyMHBvdHRlcnklMjB3YXJtJTIwYmVpZ2V8ZW58MHx8fHwxNzg4NzU1MTc4fDA&ixlib=rb-4.1.0&q=85";

const guide = [
  {
    icon: CalendarClock,
    title: "A complimentary first call",
    text: "A free 15-minute introductory call — a chance to meet briefly, talk about what has brought you to therapy, and see whether my way of working feels like a good fit.",
  },
  {
    icon: Wallet,
    title: "Fees",
    text: "I practise in both the UK and India, so fees vary by location. Get in touch and I'll share my current rates for where you are.",
  },
  {
    icon: Lock,
    title: "Confidentiality",
    text: "Everything you share is held in strict confidence, within the limits of the NCPS ethical framework.",
  },
];

const Services = () => (
  <section id="services" className="bg-[#F3EFEA] py-20 md:py-32" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <Reveal>
        <p className="mb-4 font-mono text-xs uppercase tracking-[0.22em] text-[#8C827A]">Services</p>
        <h2 className="max-w-3xl font-serif text-3xl font-normal tracking-tight text-[#2C2825] sm:text-4xl lg:text-5xl">
          Therapy that meets you <span className="italic text-[#C46B4E]">where you are</span>
        </h2>
        <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-[#5E564F] sm:text-lg">
          I currently work online only — the same depth of therapy, held over secure video,
          from the privacy and comfort of your own space.
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <article
          data-testid="service-card-telehealth"
          className="group mt-14 grid overflow-hidden rounded-3xl border border-[#E3DCD3] bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-25px_rgba(44,40,37,0.25)] md:grid-cols-2"
        >
          <div className="h-64 overflow-hidden md:h-full">
            <img
              src={CARD_IMG}
              alt="Warm, minimalist ceramics beside a bright window"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
          <div className="flex flex-col p-8 sm:p-12">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-[#C46B4E]">Online · One to one</p>
            <h3 className="mb-4 font-serif text-2xl font-medium text-[#2C2825] sm:text-3xl">
              Individual Therapy — Online
            </h3>
            <p className="flex-1 text-base font-light leading-relaxed text-[#5E564F] sm:text-lg">
              One-hour sessions held over secure video, at a consistent time each week.
              A steady, confidential space for deeper relational work — anxiety, identity,
              relationships, loss and life transitions — available across the UK and
              international time zones.
            </p>
            <p className="mt-8 border-t border-[#E3DCD3] pt-5 font-mono text-[11px] uppercase tracking-[0.16em] text-[#8C827A]">
              Secure video · 60 min · Weekly
            </p>
          </div>
        </article>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-8 rounded-3xl border border-[#E3DCD3] bg-white p-8 sm:p-12 lg:p-16" data-testid="intro-call-block">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <p className="mb-4 font-mono text-xs uppercase tracking-[0.22em] text-[#8C827A]">Beginning, gently</p>
              <h3 className="font-serif text-2xl font-medium leading-snug text-[#2C2825] sm:text-3xl">
                A complimentary <span className="italic text-[#C46B4E]">15-minute</span> introductory call
              </h3>
            </div>
            <div className="lg:col-span-8">
              <p className="text-base font-light leading-relaxed text-[#5E564F] sm:text-lg">
                Choosing a therapist is a personal decision, and finding someone you feel
                comfortable with matters. This isn&rsquo;t a therapy session or an assessment,
                and there is no expectation to commit to working together — it&rsquo;s simply a
                chance for you to get a feel for me, ask any questions you may have, and see
                whether my way of working feels like a good fit for you.
              </p>
              <p className="mt-5 text-base font-light leading-relaxed text-[#5E564F] sm:text-lg">
                If we decide to work together, our first full session will give us more space to
                explore your experiences, what you would like support with, and begin thinking
                together about what therapy might look like for you.
              </p>
              <a
                href="#contact"
                data-testid="intro-call-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#C46B4E]/40 px-6 py-3 text-sm font-medium text-[#C46B4E] transition-all duration-300 hover:border-[#C46B4E] hover:bg-[#C46B4E] hover:text-[#FAF8F5]"
              >
                Arrange your introductory call
              </a>
            </div>
          </div>
        </div>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
        {guide.map((g, i) => (
          <Reveal key={g.title} delay={i * 0.1}>
            <div className="h-full rounded-2xl border border-[#E3DCD3] bg-[#FAF8F5] p-7" data-testid={`guide-${i + 1}`}>
              <g.icon className="mb-4 h-5 w-5 text-[#C46B4E]" strokeWidth={1.5} />
              <h4 className="mb-2 text-lg font-semibold text-[#2C2825]">{g.title}</h4>
              <p className="text-sm leading-relaxed text-[#5E564F]">{g.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Services;
